import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSiteCollaborators, deleteSiteCollaborator, inviteSiteCollaborator } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useParams } from "react-router-dom";

interface Collaborator {
  id: number;
  email: string;
  avatar_url?: string | null;
}

export default function SiteCollaborators() {
  const { siteSlug } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);

  const {
    data: collaborators,
    isLoading,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: ["site-collaborators", siteSlug],
    queryFn: () => getSiteCollaborators(siteSlug as string),
    enabled: !!siteSlug,
  });

  const deleteMutation = useMutation({
    mutationFn: (collaboratorId: number) =>
      deleteSiteCollaborator(siteSlug as string, collaboratorId),
    onSuccess: () => {
      toast.success("Collaborator deleted");
      queryClient.invalidateQueries({ queryKey: ["site-collaborators", siteSlug] });
    },
    onError: (err: any) => {
      setError(err.message || "Failed to delete collaborator");
    },
  });

  const inviteMutation = useMutation({
    mutationFn: (email: string) =>
      inviteSiteCollaborator(siteSlug as string, email),
    onSuccess: () => {
      toast.success("Invitation sent");
      setInviteOpen(false);
      setInviteEmail("");
      setInviteError(null);
      queryClient.invalidateQueries({ queryKey: ["site-collaborators", siteSlug] });
    },
    onError: (err: any) => {
      setInviteError(err.message || "Failed to send invitation");
    },
  });

  return (
    <div className="max-w-4xl- mx-auto- mt-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Site collaborators</h1>
        <Button
          className="font-semibold"
          onClick={() => setInviteOpen(true)}
        >
          New manager
        </Button>
      </div>
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-white">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 2 }).map((_, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : ""}>
                      <td className="px-6 py-4">
                        <Skeleton className="h-10 w-10 rounded-full" />
                      </td>
                      <td className="px-6 py-4">
                        <Skeleton className="h-4 w-40" />
                      </td>
                      <td className="px-6 py-4">
                        <Skeleton className="h-4 w-16" />
                      </td>
                    </tr>
                  ))
                : collaborators &&
                  collaborators.map((collab: Collaborator, i: number) => (
                    <tr
                      key={collab.id}
                      className={i % 2 === 1 ? "bg-gray-50" : ""}
                    >
                      <td className="px-6 py-4">
                        {collab.avatar_url ? (
                          <img
                            src={collab.avatar_url}
                            alt={collab.email}
                            className="h-10 w-10 rounded-full object-cover border"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 text-xl font-bold">
                            <span>
                              {collab.email?.[0]?.toUpperCase() || "?"}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 font-medium">{collab.email}</td>
                      <td className="px-6 py-4">
                        <button
                          className="text-sm font-semibold text-red-600 hover:underline"
                          onClick={() => deleteMutation.mutate(collab.id)}
                          disabled={deleteMutation.isLoading}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
        {(isError || error) && (
          <Alert variant="destructive" className="mt-4">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error || (queryError as Error)?.message || "Failed to load collaborators"}
            </AlertDescription>
          </Alert>
        )}
      </Card>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite new manager</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={e => {
              e.preventDefault();
              setInviteError(null);
              inviteMutation.mutate(inviteEmail);
            }}
          >
            <Input
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              required
              className="mb-4"
              autoFocus
            />
            {inviteError && (
              <Alert variant="destructive" className="mb-4">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{inviteError}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setInviteOpen(false);
                  setInviteEmail("");
                  setInviteError(null);
                }}
                disabled={inviteMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={inviteMutation.isLoading || !inviteEmail}
              >
                {inviteMutation.isLoading ? "Inviting..." : "Invite"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
